/**
 * ChannelTypes - Página de gerenciamento de tipos de canal (WhatsApp QR, WhatsApp Oficial, etc.)
 * Permite CRUD completo de channel_types para uso no sistema de planos
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, MessageSquare, Crown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';

// Interface para tipos de canal
interface ChannelType {
  id: string;
  slug: string;
  name: string;
  display_name: string;
  description: string | null;
  icon: string | null;
  is_active: boolean;
  requires_enterprise: boolean;
  sort_order: number;
}

type ChannelTypeFormData = Omit<ChannelType, 'id'> & { id?: string };

const emptyChannelType: ChannelTypeFormData = {
  slug: '',
  name: '',
  display_name: '',
  description: '',
  icon: 'MessageSquare',
  is_active: true,
  requires_enterprise: false,
  sort_order: 0,
};

const AdminChannelTypes = () => {
  const [channelTypes, setChannelTypes] = useState<ChannelType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ChannelType | null>(null);
  const [formData, setFormData] = useState<ChannelTypeFormData>(emptyChannelType);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    fetchChannelTypes();
  }, []);

  // Busca todos os tipos de canal
  const fetchChannelTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('channel_types')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setChannelTypes(data || []);
    } catch (error) {
      console.error('Erro ao buscar tipos de canal:', error);
      showToast.error('Não foi possível carregar os tipos de canal');
    } finally {
      setLoading(false);
    }
  };

  // Abre dialog para criar novo
  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData(emptyChannelType);
    setDialogOpen(true);
  };

  // Abre dialog para editar
  const openEditDialog = (item: ChannelType) => {
    setEditingItem(item);
    setFormData(item);
    setDialogOpen(true);
  };

  // Salva (cria ou atualiza)
  const handleSubmit = async () => {
    if (!formData.slug || !formData.name || !formData.display_name) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const payload = {
        slug: formData.slug.toLowerCase().replace(/\s+/g, '_'),
        name: formData.name,
        display_name: formData.display_name,
        description: formData.description || null,
        icon: formData.icon || null,
        is_active: formData.is_active,
        requires_enterprise: formData.requires_enterprise,
        sort_order: formData.sort_order,
      };

      if (editingItem) {
        const { error } = await supabase
          .from('channel_types')
          .update(payload)
          .eq('id', editingItem.id);

        if (error) throw error;
        showToast.success('Tipo de canal atualizado');
      } else {
        const { error } = await supabase
          .from('channel_types')
          .insert(payload);

        if (error) {
          if (error.message.includes('duplicate')) {
            showToast.error('Já existe um tipo de canal com este slug');
            return;
          }
          throw error;
        }
        showToast.success('Tipo de canal criado');
      }

      setDialogOpen(false);
      fetchChannelTypes();
    } catch (error) {
      console.error('Erro ao salvar tipo de canal:', error);
      showToast.error('Não foi possível salvar o tipo de canal');
    }
  };

  // Deleta tipo de canal
  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este tipo de canal?')) return;

    setDeleting(id);
    try {
      const { error } = await supabase
        .from('channel_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showToast.success('Tipo de canal excluído');
      fetchChannelTypes();
    } catch (error) {
      console.error('Erro ao excluir tipo de canal:', error);
      showToast.error('Não foi possível excluir. Pode estar em uso.');
    } finally {
      setDeleting(null);
    }
  };

  // Toggle ativo/inativo
  const toggleActive = async (item: ChannelType) => {
    try {
      const { error } = await supabase
        .from('channel_types')
        .update({ is_active: !item.is_active })
        .eq('id', item.id);

      if (error) throw error;
      showToast.success(`Tipo de canal ${!item.is_active ? 'ativado' : 'desativado'}`);
      fetchChannelTypes();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      showToast.error('Não foi possível alterar o status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tipos de Canal</h1>
          <p className="text-muted-foreground">Gerencie os tipos de canal disponíveis no sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Tipo de Canal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar Tipo de Canal' : 'Novo Tipo de Canal'}</DialogTitle>
              <DialogDescription>
                {editingItem ? 'Atualize as informações do tipo de canal' : 'Configure um novo tipo de canal'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    placeholder="whatsapp_qr"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    disabled={!!editingItem}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Interno *</Label>
                  <Input
                    id="name"
                    placeholder="WhatsApp QR"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_name">Nome de Exibição *</Label>
                <Input
                  id="display_name"
                  placeholder="WhatsApp (QR Code)"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="Conexão via escaneamento de QR Code"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="icon">Ícone (Lucide)</Label>
                  <Input
                    id="icon"
                    placeholder="MessageSquare"
                    value={formData.icon || ''}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sort_order">Ordem</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Ativo</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="requires_enterprise"
                    checked={formData.requires_enterprise}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_enterprise: checked })}
                  />
                  <Label htmlFor="requires_enterprise">Requer Enterprise</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>
                {editingItem ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Tipos de Canal
          </CardTitle>
          <CardDescription>
            {channelTypes.length} tipos cadastrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : channelTypes.length === 0 ? (
            <p className="text-muted-foreground">Nenhum tipo de canal cadastrado</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead>Nome de Exibição</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-center">Enterprise</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Ordem</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {channelTypes.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{item.slug}</code>
                    </TableCell>
                    <TableCell className="font-medium">{item.display_name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {item.description || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.requires_enterprise ? (
                        <Badge variant="secondary" className="gap-1">
                          <Crown className="h-3 w-3" />
                          Enterprise
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={item.is_active ? 'default' : 'secondary'}
                        className="cursor-pointer"
                        onClick={() => toggleActive(item)}
                      >
                        {item.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{item.sort_order}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(item)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {/* Botão de exclusão removido - channel_types é parte crítica do sistema */}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminChannelTypes;
